import React from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { BlogPost } from '../types/blog';
import blogService from '../services/blogService';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import BlogCard from '../components/blog/BlogCard';
import { Layout } from '../components/layout/Layout';
import { SEOHead } from "../components/seo/SEOHead";
import { AdBlock } from '../components/ads/AdBlock';
import { NativeBanner } from '../components/ads/NativeBanner';
import { useTranslation } from "react-i18next";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";
import HighPerformanceBanner from '../components/ads/HighPerformanceBanner';
import { BottomAdWrapper } from '../components/ads/BottomAdWrapper';
import HilltopAd from '../components/ads/HilltopAd';

const BlogContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  
  @media (max-width: 768px) {
    padding: 0 1rem 1rem;
    gap: var(--spacing-1);
  }
`;

const AdContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  margin: var(--spacing-1) 0;
  
  @media (max-width: 768px) {
    margin: 0.5rem 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  margin-top: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--color-text-primary);
  margin: 0;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: var(--color-primary);
  color: white;
  border-radius: 8px;
  text-decoration: none;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;

  &:hover {
    background-color: var(--color-primary-dark);
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
`;

const ErrorMessage = styled.div`
  color: var(--color-error);
  text-align: center;
  padding: 2rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  margin-top: 2rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: var(--color-text-secondary);
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Blog = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const queryClient = useQueryClient();
  const { t } = useTranslation('blog');
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();

  const { data: posts, isLoading, error } = useQuery<BlogPost[], Error>({
    queryKey: ['blog-posts'],
    queryFn: blogService.getAllPosts,
  });

  if (isLoading) {
    return (
      <Layout>
        <SEOHead
          title={t('title')}
          description={t('description')}
          type="website"
        />
        <BlogContainer>
          <AdContainer>
            <AdBlock format="banner" />
          </AdContainer>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
          <AdContainer>
            <AdBlock format="native" />
          </AdContainer>
        </BlogContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <SEOHead
          title={t('title')}
          description={t('description')}
          type="website"
        />
        <BlogContainer>
          <AdContainer>
            <AdBlock format="banner" />
          </AdContainer>
          <ErrorMessage>
            {t('error', { error: error.message })}
          </ErrorMessage>
          <AdContainer>
            <AdBlock format="native" />
          </AdContainer>
        </BlogContainer>
      </Layout>
    );
  }

  return (
    <Layout>
        <SEOHead
          title={t('title')}
          description={t('description')}
          type="website"
        />
        <BlogContainer>
        {/* Publicité en haut - une seule */}
        <AdContainer>
          <HighPerformanceBanner 
            adKey="2154f7f7df439957dfc24240cd37dd3c"
            width={728}
            height={90}
            id="hp-banner-blog-top"
            hideOnMobile
          />
        </AdContainer>

        <Header>
          <Title>{t('heading')}</Title>
          {user?.role === 'admin' && (
            <Link to="/blog/new">
              <Button>
                <Plus size={20} />
                {t('newPost')}
              </Button>
            </Link>
          )}
        </Header>

        <BlogGrid>
          {posts && posts.length > 0 ? (
            posts.map((post) => (
              <BlogCard key={post.id} post={post} />
            ))
          ) : (
            <EmptyMessage>{t('empty')}</EmptyMessage>
          )}
        </BlogGrid>

        <AdContainer>
          <AdBlock format="native" />
        </AdContainer>
        
        {/* Publicité HighPerformance en bas */}
        <HighPerformanceBanner 
          adKey="2154f7f7df439957dfc24240cd37dd3c"
          width={728}
          height={90}
          id="hp-banner-blog-bottom"
        />
      </BlogContainer>
    </Layout>
  );
};

export default Blog;
